import {
  computeEasyThresholdSecondsFromReviews,
  FSRS_RATING_AGAIN as RATING_AGAIN,
  FSRS_RATING_EASY as RATING_EASY,
  FSRS_RATING_GOOD as RATING_GOOD,
  FSRS_RATING_HARD as RATING_HARD,
  getStoredOrDerivedFsrsRating,
} from "./fsrsReviewRating";

const SECONDS_PER_DAY = 86400;
const DEFAULT_PLANNER_TARGET = 0.9;
const DEFAULT_ENABLE_FUZZ = true;
const DEFAULT_ENABLE_SHORT_TERM = true;
const DEFAULT_MAXIMUM_INTERVAL_DAYS = 36500;
const DEFAULT_LEARNING_STEPS_MINUTES = [1, 10];
const DEFAULT_RELEARNING_STEPS_MINUTES = [10];
const DEFAULT_FSRS_PARAMETERS = [
  0.212,
  1.2931,
  2.3065,
  8.2956,
  6.4133,
  0.8334,
  3.0194,
  0.001,
  1.8722,
  0.1666,
  0.796,
  1.4835,
  0.0614,
  0.2629,
  1.6483,
  0.6014,
  1.8729,
  0.5425,
  0.0912,
  0.0658,
  0.1542,
];
const LOWER_BOUNDS_PARAMETERS = [
  0.001,
  0.001,
  0.001,
  0.001,
  1.0,
  0.001,
  0.001,
  0.001,
  0.0,
  0.0,
  0.001,
  0.001,
  0.001,
  0.001,
  0.0,
  0.0,
  1.0,
  0.0,
  0.0,
  0.0,
  0.1,
];
const UPPER_BOUNDS_PARAMETERS = [
  100.0,
  100.0,
  100.0,
  100.0,
  10.0,
  4.0,
  4.0,
  0.75,
  4.5,
  0.8,
  3.5,
  5.0,
  0.25,
  0.9,
  4.0,
  1.0,
  6.0,
  2.0,
  2.0,
  0.8,
  0.8,
];
const MIN_DIFFICULTY = 1.0;
const MAX_DIFFICULTY = 10.0;
const STABILITY_MIN = 0.001;
const FUZZ_RANGES = [
  { start: 2.5, end: 7.0, factor: 0.15 },
  { start: 7.0, end: 20.0, factor: 0.1 },
  { start: 20.0, end: Number.POSITIVE_INFINITY, factor: 0.05 },
];

const clamp = (value, min, max) =>
  Math.min(Math.max(Number(value), min), max);

const roundNumber = (value, digits = 6) =>
  Number(Number(value).toFixed(digits));

export const normalizeSchedulerModel = (value) =>
  String(value ?? "FSRS").trim().toUpperCase() === "ML" ? "ML" : "FSRS";

export const normalizePlannerTarget = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return DEFAULT_PLANNER_TARGET;
  }
  return clamp(numericValue, 0.5, 0.99);
};

export const normalizeBooleanFlag = (value, fallback) => {
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "1", "yes", "on"].includes(normalized)) {
      return true;
    }
    if (["false", "0", "no", "off"].includes(normalized)) {
      return false;
    }
  }
  return fallback;
};

export const normalizeStepMinutes = (values, fallback = []) => {
  const numericValues = (Array.isArray(values) ? values : [])
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value) && value > 0)
    .map((value) => roundNumber(value, 4));
  return numericValues.length > 0 ? numericValues : [...fallback];
};

export const normalizeFsrsParameters = (values) => {
  const rawValues = Array.isArray(values) ? values : [];
  return DEFAULT_FSRS_PARAMETERS.map((defaultValue, index) => {
    const nextValue = Number(rawValues[index]);
    const boundedValue = Number.isFinite(nextValue) ? nextValue : defaultValue;
    return clamp(
      boundedValue,
      LOWER_BOUNDS_PARAMETERS[index],
      UPPER_BOUNDS_PARAMETERS[index]
    );
  });
};

export const normalizeFsrsConfig = (config = {}) => ({
  desiredRetention: normalizePlannerTarget(
    config?.desiredRetention ?? config?.plannerTarget ?? DEFAULT_PLANNER_TARGET
  ),
  fuzz: normalizeBooleanFlag(config?.fuzz, DEFAULT_ENABLE_FUZZ),
  enableShortTerm: normalizeBooleanFlag(
    config?.enableShortTerm,
    DEFAULT_ENABLE_SHORT_TERM
  ),
  learningStepsMinutes: normalizeStepMinutes(
    config?.learningStepsMinutes,
    DEFAULT_LEARNING_STEPS_MINUTES
  ),
  relearningStepsMinutes: normalizeStepMinutes(
    config?.relearningStepsMinutes,
    DEFAULT_RELEARNING_STEPS_MINUTES
  ),
  maximumIntervalDays: Math.max(
    1,
    Math.round(
      Number(config?.maximumIntervalDays) || DEFAULT_MAXIMUM_INTERVAL_DAYS
    )
  ),
  easyThresholdSeconds: Number.isFinite(Number(config?.easyThresholdSeconds))
    ? Number(config.easyThresholdSeconds)
    : null,
});

const getActionKey = (cardId, sideShown = false) =>
  `${Number(cardId)}:${sideShown ? "back" : "front"}`;

const getSubmittedTime = (review) => Number(review?.submitted_time ?? 0);

const getReviewId = (review) => Number(review?.review_id ?? review?.review ?? 0);

const sortReviews = (reviews) =>
  [...(reviews ?? [])].sort((left, right) => {
    const reviewGap = getReviewId(left) - getReviewId(right);
    if (reviewGap !== 0) {
      return reviewGap;
    }
    return getSubmittedTime(left) - getSubmittedTime(right);
  });

const hashString = (value) => {
  let hash = 2166136261;
  const input = String(value ?? "");
  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
};

const seededUnitRandom = (seed) => hashString(seed) / 4294967295;

const clampDifficulty = (difficulty) =>
  clamp(difficulty, MIN_DIFFICULTY, MAX_DIFFICULTY);

const clampStability = (stability) =>
  Math.max(Number(stability) || 0, STABILITY_MIN);

const getReviewRating = (review, easyThresholdSeconds = null) =>
  getStoredOrDerivedFsrsRating(review, easyThresholdSeconds);

const buildForgettingFactor = (weights) =>
  Math.pow(0.9, -1 / weights[20]) - 1;

const initialStability = (weights, rating) =>
  clampStability(weights[rating - 1]);

const initialDifficulty = (weights, rating, shouldClamp = true) => {
  const value = weights[4] - Math.exp(weights[5] * (rating - 1)) + 1;
  return shouldClamp ? clampDifficulty(value) : value;
};

const nextDifficulty = (weights, difficulty, rating) => {
  const deltaDifficulty = -(weights[6] * (rating - RATING_GOOD));
  const dampedDifficulty =
    difficulty + ((10 - difficulty) * deltaDifficulty) / 9;
  const revertedDifficulty =
    weights[7] * initialDifficulty(weights, RATING_EASY, false) +
    (1 - weights[7]) * dampedDifficulty;
  return clampDifficulty(revertedDifficulty);
};

const shortTermStability = (weights, stability, rating) => {
  let increase =
    Math.exp(weights[17] * (rating - RATING_GOOD + weights[18])) *
    Math.pow(stability, -weights[19]);
  if ([RATING_HARD, RATING_GOOD, RATING_EASY].includes(rating)) {
    increase = Math.max(increase, 1.0);
  }
  return clampStability(stability * increase);
};

const nextForgetStability = (weights, difficulty, stability, retrievability) => {
  const longTermValue =
    weights[11] *
    Math.pow(difficulty, -weights[12]) *
    (Math.pow(stability + 1, weights[13]) - 1) *
    Math.exp((1 - retrievability) * weights[14]);
  const shortTermCap = stability / Math.exp(weights[17] * weights[18]);
  return clampStability(Math.min(longTermValue, shortTermCap));
};

const nextRecallStability = (
  weights,
  difficulty,
  stability,
  retrievability,
  rating
) => {
  const hardPenalty = rating === RATING_HARD ? weights[15] : 1;
  const easyBonus = rating === RATING_EASY ? weights[16] : 1;
  const nextValue =
    stability *
    (1 +
      Math.exp(weights[8]) *
        (11 - difficulty) *
        Math.pow(stability, -weights[9]) *
        (Math.exp((1 - retrievability) * weights[10]) - 1) *
        hardPenalty *
        easyBonus);
  return clampStability(nextValue);
};

const nextStability = (weights, difficulty, stability, retrievability, rating) => {
  if (rating === RATING_AGAIN) {
    return nextForgetStability(weights, difficulty, stability, retrievability);
  }
  return nextRecallStability(
    weights,
    difficulty,
    stability,
    retrievability,
    rating
  );
};

const nextIntervalDays = (weights, stability, desiredRetention, maximumIntervalDays) => {
  const factor = buildForgettingFactor(weights);
  const intervalDays =
    (stability / factor) *
    (Math.pow(desiredRetention, 1 / -weights[20]) - 1);
  return clamp(
    Math.round(intervalDays),
    1,
    maximumIntervalDays
  );
};

let fsrsBrowserModule = null;
let fsrsBrowserModulePromise = null;
const fsrsSchedulerCache = new Map();
const fsrsRuntimeModeLogged = new Set();
const fsrsOptimizerModeLogged = new Set();

const canUseFsrsBrowser = () =>
  typeof window !== "undefined" &&
  typeof window.Worker !== "undefined" &&
  typeof WebAssembly !== "undefined";

const logFsrsModeOnce = (bucket, message) => {
  if (bucket.has(message)) {
    return;
  }
  bucket.add(message);
  console.info(message);
};

const getFsrsSchedulerCacheKey = (weights) =>
  normalizeFsrsParameters(weights).map((weight) => roundNumber(weight, 6)).join(":");

const getFsrsBrowserScheduler = (weights) => {
  if (!fsrsBrowserModule?.Fsrs) {
    logFsrsModeOnce(fsrsRuntimeModeLogged, "[fsrs] runtime=local-fallback");
    return null;
  }
  const cacheKey = getFsrsSchedulerCacheKey(weights);
  if (!fsrsSchedulerCache.has(cacheKey)) {
    fsrsSchedulerCache.set(
      cacheKey,
      new fsrsBrowserModule.Fsrs(Float32Array.from(normalizeFsrsParameters(weights)))
    );
    if (fsrsSchedulerCache.size > 4) {
      const oldestKey = fsrsSchedulerCache.keys().next().value;
      fsrsSchedulerCache.get(oldestKey)?.free?.();
      fsrsSchedulerCache.delete(oldestKey);
    }
  }
  return fsrsSchedulerCache.get(cacheKey) ?? null;
};

const getCanonicalNextStates = ({
  stability,
  difficulty,
  desiredRetention,
  elapsedDays,
  weights,
}) => {
  const scheduler = getFsrsBrowserScheduler(weights);
  if (!scheduler) {
    return null;
  }
  try {
    return scheduler.nextStates(
      Number.isFinite(stability) ? stability : null,
      Number.isFinite(difficulty) ? difficulty : null,
      normalizePlannerTarget(desiredRetention),
      Math.max(Number(elapsedDays) || 0, 0)
    );
  } catch (error) {
    console.error("fsrs-browser nextStates failed", error);
    return null;
  }
};

const getCanonicalNextIntervalDays = ({
  stability,
  desiredRetention,
  rating,
  weights,
  maximumIntervalDays,
}) => {
  const scheduler = getFsrsBrowserScheduler(weights);
  if (!scheduler) {
    return null;
  }
  try {
    return clamp(
      Math.round(
        scheduler.nextInterval(
          Number.isFinite(stability) ? stability : null,
          normalizePlannerTarget(desiredRetention),
          rating
        )
      ),
      1,
      maximumIntervalDays
    );
  } catch (error) {
    console.error("fsrs-browser nextInterval failed", error);
    return null;
  }
};

const getCanonicalRatingState = (nextStates, rating) => {
  if (!nextStates) {
    return null;
  }
  if (rating === RATING_AGAIN) {
    return nextStates.again ?? null;
  }
  if (rating === RATING_HARD) {
    return nextStates.hard ?? null;
  }
  if (rating === RATING_EASY) {
    return nextStates.easy ?? null;
  }
  return nextStates.good ?? null;
};

export const initFsrsBrowser = async () => {
  if (!canUseFsrsBrowser()) {
    logFsrsModeOnce(
      fsrsRuntimeModeLogged,
      "[fsrs] runtime=local-fallback (fsrs-browser unavailable)"
    );
    return false;
  }
  if (!fsrsBrowserModulePromise) {
    fsrsBrowserModulePromise = import("fsrs-browser/fsrs_browser.js")
      .then(async (module) => {
        await module.default();
        fsrsBrowserModule = module;
        logFsrsModeOnce(fsrsRuntimeModeLogged, "[fsrs] runtime=fsrs-browser");
        return module;
      })
      .catch((error) => {
        console.error("Failed to initialize fsrs-browser", error);
        logFsrsModeOnce(
          fsrsRuntimeModeLogged,
          "[fsrs] runtime=local-fallback (fsrs-browser init failed)"
        );
        return null;
      });
  }
  return !!(await fsrsBrowserModulePromise);
};

export const predictRetrievability = (
  elapsedDays,
  stabilityDays,
  weights = DEFAULT_FSRS_PARAMETERS
) => {
  if (!(elapsedDays > 0) || !(stabilityDays > 0)) {
    return 1;
  }
  const factor = buildForgettingFactor(weights);
  return clamp(
    Math.pow(1 + (factor * elapsedDays) / stabilityDays, -weights[20]),
    0,
    1
  );
};

export const getIntervalDaysForTarget = (
  stabilityDays,
  desiredRetention = DEFAULT_PLANNER_TARGET,
  weights = DEFAULT_FSRS_PARAMETERS,
  maximumIntervalDays = DEFAULT_MAXIMUM_INTERVAL_DAYS
) =>
  nextIntervalDays(
    weights,
    clampStability(stabilityDays),
    normalizePlannerTarget(desiredRetention),
    maximumIntervalDays
  );

const getDeterministicFuzzedIntervalDays = (
  intervalDays,
  maximumIntervalDays,
  seed
) => {
  if (!(intervalDays >= 2.5)) {
    return intervalDays;
  }

  let delta = 1.0;
  FUZZ_RANGES.forEach((range) => {
    delta +=
      range.factor *
      Math.max(Math.min(Number(intervalDays), range.end) - range.start, 0);
  });

  const minimumInterval = Math.min(
    Math.max(2, Math.round(intervalDays - delta)),
    Math.min(Math.round(intervalDays + delta), maximumIntervalDays)
  );
  const maximumInterval = Math.min(
    Math.round(intervalDays + delta),
    maximumIntervalDays
  );
  if (maximumInterval <= minimumInterval) {
    return minimumInterval;
  }

  const randomUnit = seededUnitRandom(seed);
  return Math.min(
    Math.round(randomUnit * (maximumInterval - minimumInterval + 1) + minimumInterval),
    maximumIntervalDays
  );
};

const getFuzzRangeDays = (intervalDays, maximumIntervalDays) => {
  if (!(intervalDays >= 2.5)) {
    const rounded = Math.max(1, Math.round(intervalDays));
    return {
      minIntervalDays: rounded,
      maxIntervalDays: rounded,
    };
  }

  let delta = 1.0;
  FUZZ_RANGES.forEach((range) => {
    delta +=
      range.factor *
      Math.max(Math.min(Number(intervalDays), range.end) - range.start, 0);
  });

  const minIntervalDays = Math.max(2, Math.round(intervalDays - delta));
  const maxIntervalDays = Math.min(
    Math.round(intervalDays + delta),
    maximumIntervalDays
  );
  return {
    minIntervalDays: Math.min(minIntervalDays, maxIntervalDays),
    maxIntervalDays,
  };
};

const formatDueIntervalParts = (seconds) => {
  const totalSeconds = Math.max(Number(seconds) || 0, 0);
  const totalMinutes = totalSeconds / 60;
  const totalHours = totalSeconds / 3600;
  const totalDays = totalSeconds / SECONDS_PER_DAY;
  const wholeDays = Math.floor(totalSeconds / SECONDS_PER_DAY);
  const wholeHours = Math.floor((totalSeconds % SECONDS_PER_DAY) / 3600);
  const wholeMinutes = Math.floor((totalSeconds % 3600) / 60);
  return {
    totalDays: roundNumber(totalDays, 4),
    totalHours: roundNumber(totalHours, 4),
    totalMinutes: roundNumber(totalMinutes, 4),
    wholeDays,
    wholeHours,
    wholeMinutes,
  };
};

const logDueAtAssignment = ({
  review,
  phase,
  reason,
  submittedTime,
  dueAt,
  intervalDays = null,
  scheduledIntervalDays = null,
  stepMinutes = null,
  fuzz = false,
}) => {
  const intervalSeconds = Math.max(Number(dueAt) - Number(submittedTime), 0);
  const parts = formatDueIntervalParts(intervalSeconds);
  const details = [
    `[fsrs] due_at`,
    `card=${Number(review?.card_id) || "unknown"}`,
    `phase=${phase}`,
    `reason=${reason}`,
    `days=${parts.totalDays}`,
    `hours=${parts.totalHours}`,
    `mins=${parts.totalMinutes}`,
    `breakdown=${parts.wholeDays}d${parts.wholeHours}h${parts.wholeMinutes}m`,
    `due_at=${roundNumber(Number(dueAt), 3)}`,
  ];
  if (Number.isFinite(intervalDays)) {
    details.push(`interval_days=${roundNumber(intervalDays, 4)}`);
  }
  if (Number.isFinite(scheduledIntervalDays)) {
    details.push(`scheduled_interval_days=${roundNumber(scheduledIntervalDays, 4)}`);
  }
  if (Number.isFinite(stepMinutes)) {
    details.push(`step_mins=${roundNumber(stepMinutes, 4)}`);
  }
  if (fuzz) {
    details.push(`fuzz=${fuzz}`);
  }
  console.log(details.join(" "));
};

const buildLongTermDueAt = ({
  submittedTime,
  review,
  reviewCount,
  intervalDays,
  fuzz,
  maximumIntervalDays,
}) => {
  const scheduledIntervalDays = fuzz
    ? getDeterministicFuzzedIntervalDays(
        intervalDays,
        maximumIntervalDays,
        [
          review?.card_id ?? "card",
          getSubmittedTime(review),
          reviewCount,
          intervalDays,
        ].join(":")
      )
    : intervalDays;
  return {
    dueAt: submittedTime + scheduledIntervalDays * SECONDS_PER_DAY,
    scheduledIntervalDays,
  };
};

const buildStepState = (
  state,
  review,
  submittedTime,
  phase,
  stepIndex,
  stepMinutes,
  reason,
  logDueAssignments = false
) => {
  const dueAt = submittedTime + stepMinutes * 60;
  if (logDueAssignments) {
    logDueAtAssignment({
      review,
      phase,
      reason,
      submittedTime,
      dueAt,
      stepMinutes,
    });
  }
  return {
    ...state,
    phase,
    stepIndex,
    dueAt,
    schedulingReason: reason,
    scheduledStepMinutes: stepMinutes,
    scheduledIntervalDays: null,
    targetIntervalDays: null,
  };
};

const buildReviewState = (
  state,
  submittedTime,
  review,
  intervalDays,
  config,
  reason,
  logDueAssignments = false
) => {
  const { dueAt, scheduledIntervalDays } = buildLongTermDueAt({
    submittedTime,
    review,
    reviewCount: state.reviewCount,
    intervalDays,
    fuzz: config.fuzz,
    maximumIntervalDays: config.maximumIntervalDays,
  });
  if (logDueAssignments) {
    logDueAtAssignment({
      review,
      phase: "review",
      reason,
      submittedTime,
      dueAt,
      intervalDays,
      scheduledIntervalDays,
      fuzz: !!config.fuzz,
    });
  }
  return {
    ...state,
    phase: "review",
    stepIndex: null,
    dueAt,
    schedulingReason: reason,
    scheduledStepMinutes: null,
    scheduledIntervalDays,
    targetIntervalDays: intervalDays,
  };
};

const getCurrentRecallProbability = ({
  state,
  now,
  weights,
}) => {
  if (!state) {
    return 0.5;
  }

  const elapsedDays =
    Math.max(Number(now) - Number(state.lastReviewTime ?? now), 0) /
    SECONDS_PER_DAY;
  return predictRetrievability(
    elapsedDays,
    state.stability,
    weights
  );
};

const scheduleStateAfterReview = ({
  previousState,
  nextState,
  rating,
  submittedTime,
  review,
  weights,
  config,
  logDueAssignments = false,
}) => {
  const getReviewIntervalDays = () =>
    getCanonicalNextIntervalDays({
      stability: nextState.stability,
      desiredRetention: config.desiredRetention,
      rating,
      weights,
      maximumIntervalDays: config.maximumIntervalDays,
    }) ??
    nextIntervalDays(
      weights,
      nextState.stability,
      config.desiredRetention,
      config.maximumIntervalDays
    );

  if (!config.enableShortTerm) {
    return buildReviewState(
      nextState,
      submittedTime,
      review,
      getReviewIntervalDays(),
      config,
      "short_term_disabled",
      logDueAssignments
    );
  }

  if (!previousState) {
    if (config.learningStepsMinutes.length === 0) {
      return buildReviewState(
        nextState,
        submittedTime,
        review,
        getReviewIntervalDays(),
        config,
        "new_card_no_learning_steps"
      );
    }
    if (rating === RATING_AGAIN) {
      return buildStepState(
        nextState,
        review,
        submittedTime,
        "learning",
        0,
        config.learningStepsMinutes[0],
        "new_card_again_enters_learning",
        logDueAssignments
      );
    }
    if (rating === RATING_EASY) {
      return buildReviewState(
        nextState,
        submittedTime,
        review,
        getReviewIntervalDays(),
        config,
        "new_card_easy_graduates_to_review",
        logDueAssignments
      );
    }
    const firstGoodStepIndex =
      config.learningStepsMinutes.length > 1 ? 1 : 0;
    return buildStepState(
      nextState,
      review,
      submittedTime,
      "learning",
      firstGoodStepIndex,
      config.learningStepsMinutes[firstGoodStepIndex],
      "new_card_correct_enters_learning",
      logDueAssignments
    );
  }

  if (previousState.phase === "learning") {
    if (rating === RATING_AGAIN) {
      return buildStepState(
        nextState,
        review,
        submittedTime,
        "learning",
        0,
        config.learningStepsMinutes[0],
        "learning_again_resets_to_first_step",
        logDueAssignments
      );
    }
    if (rating === RATING_EASY) {
      return buildReviewState(
        nextState,
        submittedTime,
        review,
        nextIntervalDays(
          weights,
          nextState.stability,
          config.desiredRetention,
          config.maximumIntervalDays
        ),
        config,
        "learning_easy_graduates_to_review",
        logDueAssignments
      );
    }
    const currentStepIndex = Number(previousState.stepIndex ?? 0);
    const nextStepIndex = currentStepIndex + 1;
    if (nextStepIndex < config.learningStepsMinutes.length) {
      return buildStepState(
        nextState,
        review,
        submittedTime,
        "learning",
        nextStepIndex,
        config.learningStepsMinutes[nextStepIndex],
        "learning_correct_advances_step",
        logDueAssignments
      );
    }
    return buildReviewState(
      nextState,
      submittedTime,
      review,
      getReviewIntervalDays(),
      config,
      "learning_graduates_to_review",
      logDueAssignments
    );
  }

  if (previousState.phase === "relearning") {
    if (config.relearningStepsMinutes.length === 0) {
      return buildReviewState(
        nextState,
        submittedTime,
        review,
        getReviewIntervalDays(),
        config,
        "relearning_disabled_returns_to_review",
        logDueAssignments
      );
    }
    if (rating === RATING_AGAIN) {
      return buildStepState(
        nextState,
        review,
        submittedTime,
        "relearning",
        0,
        config.relearningStepsMinutes[0],
        "relearning_again_resets_to_first_step",
        logDueAssignments
      );
    }
    const currentStepIndex = Number(previousState.stepIndex ?? 0);
    const nextStepIndex = currentStepIndex + 1;
    if (nextStepIndex < config.relearningStepsMinutes.length) {
      return buildStepState(
        nextState,
        review,
        submittedTime,
        "relearning",
        nextStepIndex,
        config.relearningStepsMinutes[nextStepIndex],
        "relearning_correct_advances_step",
        logDueAssignments
      );
    }
    return buildReviewState(
      nextState,
      submittedTime,
      review,
      getReviewIntervalDays(),
      config,
      "relearning_graduates_to_review",
      logDueAssignments
    );
  }

  if (rating === RATING_AGAIN && config.relearningStepsMinutes.length > 0) {
    return buildStepState(
      nextState,
      review,
      submittedTime,
      "relearning",
      0,
      config.relearningStepsMinutes[0],
      "review_lapse_enters_relearning",
      logDueAssignments
    );
  }

  return buildReviewState(
    nextState,
    submittedTime,
    review,
    getReviewIntervalDays(),
    config,
    "review_state_continues",
    logDueAssignments
  );
};

export const advanceFsrsState = ({
  state,
  review,
  weights,
  desiredRetention = DEFAULT_PLANNER_TARGET,
  config = {},
  logDueAssignments = false,
}) => {
  const normalizedWeights = normalizeFsrsParameters(weights);
  const normalizedConfig = normalizeFsrsConfig({
    ...config,
    desiredRetention,
  });
  const submittedTime = getSubmittedTime(review);
  const rating = getReviewRating(review, normalizedConfig.easyThresholdSeconds);
  const canonicalNewState = getCanonicalRatingState(
    getCanonicalNextStates({
      stability: null,
      difficulty: null,
      desiredRetention,
      elapsedDays: 0,
      weights: normalizedWeights,
    }),
    rating
  );

  if (!state) {
    const nextState = {
      difficulty:
        Number(canonicalNewState?.memory?.difficulty) ||
        initialDifficulty(normalizedWeights, rating, true),
      stability:
        Number(canonicalNewState?.memory?.stability) ||
        initialStability(normalizedWeights, rating),
      dueAt: null,
      lastReviewTime: submittedTime,
      reviewCount: 1,
      lapseCount: rating === RATING_AGAIN ? 1 : 0,
      phase: "new",
      stepIndex: null,
    };
    return scheduleStateAfterReview({
      previousState: null,
      nextState,
      rating,
      submittedTime,
      review,
      weights: normalizedWeights,
      config: normalizedConfig,
      logDueAssignments,
    });
  }

  const elapsedDaysRaw =
    Math.max(submittedTime - Number(state.lastReviewTime ?? submittedTime), 0) /
    SECONDS_PER_DAY;
  const isSameDayReview =
    normalizedConfig.enableShortTerm && elapsedDaysRaw < 1;
  const canonicalNextState = getCanonicalRatingState(
    getCanonicalNextStates({
      stability: state.stability,
      difficulty: state.difficulty,
      desiredRetention,
      elapsedDays: elapsedDaysRaw,
      weights: normalizedWeights,
    }),
    rating
  );
  const retrievability = predictRetrievability(
    elapsedDaysRaw,
    state.stability,
    normalizedWeights
  );
  const nextState = {
    difficulty:
      Number(canonicalNextState?.memory?.difficulty) ||
      nextDifficulty(normalizedWeights, state.difficulty, rating),
    stability:
      Number(canonicalNextState?.memory?.stability) ||
      (isSameDayReview
        ? shortTermStability(normalizedWeights, state.stability, rating)
        : nextStability(
            normalizedWeights,
            state.difficulty,
            state.stability,
            retrievability,
            rating
          )),
    dueAt: null,
    lastReviewTime: submittedTime,
    reviewCount: Number(state.reviewCount ?? 0) + 1,
    lapseCount:
      Number(state.lapseCount ?? 0) + (rating === RATING_AGAIN ? 1 : 0),
    phase: state.phase ?? "review",
    stepIndex: state.stepIndex ?? null,
  };
  return scheduleStateAfterReview({
    previousState: state,
    nextState,
    rating,
    submittedTime,
    review,
    weights: normalizedWeights,
    config: normalizedConfig,
    logDueAssignments,
  });
};

export const replayFsrsHistory = ({
  historyRows,
  sideShown = false,
  bothSidesMode = true,
  weights = DEFAULT_FSRS_PARAMETERS,
  desiredRetention = DEFAULT_PLANNER_TARGET,
  config = {},
}) => {
  const normalizedWeights = normalizeFsrsParameters(weights);
  const filteredHistory = sortReviews(historyRows).filter((review) =>
    bothSidesMode ? !!review?.side_shown === !!sideShown : true
  );
  const normalizedConfig = normalizeFsrsConfig({
    ...config,
    desiredRetention,
    easyThresholdSeconds:
      config?.easyThresholdSeconds ??
      computeEasyThresholdSecondsFromReviews(filteredHistory),
  });
  let state = null;
  filteredHistory.forEach((review) => {
    state = advanceFsrsState({
      state,
      review,
      weights: normalizedWeights,
      desiredRetention,
      config: normalizedConfig,
    });
  });

  return {
    state,
    filteredHistory,
  };
};

export const projectFsrsStateAtTime = ({
  state,
  now,
  weights = DEFAULT_FSRS_PARAMETERS,
  desiredRetention = DEFAULT_PLANNER_TARGET,
  config = {},
}) => {
  const normalizedWeights = normalizeFsrsParameters(weights);
  const normalizedConfig = normalizeFsrsConfig({
    ...config,
    desiredRetention,
  });

  if (!state) {
    return {
      state: null,
      probability: 0.5,
      dueAt: null,
      dueInMinutes: null,
      intervalDays: null,
      minIntervalDays: null,
      maxIntervalDays: null,
    };
  }

  const isReviewState = state.phase === "review";
  const intervalDays = isReviewState
    ? nextIntervalDays(
        normalizedWeights,
        state.stability,
        normalizedConfig.desiredRetention,
        normalizedConfig.maximumIntervalDays
      )
    : null;
  const fuzzRange = intervalDays == null
    ? { minIntervalDays: null, maxIntervalDays: null }
    : getFuzzRangeDays(intervalDays, normalizedConfig.maximumIntervalDays);
  const probability = getCurrentRecallProbability({
    state,
    now,
    weights: normalizedWeights,
  });

  return {
    state,
    probability,
    dueAt: Number(state.dueAt ?? now),
    dueInMinutes: (Number(state.dueAt ?? now) - Number(now)) / 60,
    intervalDays,
    minIntervalDays: fuzzRange.minIntervalDays,
    maxIntervalDays: fuzzRange.maxIntervalDays,
  };
};

export const getFsrsStateAtTime = ({
  historyRows,
  now,
  sideShown = false,
  bothSidesMode = true,
  weights = DEFAULT_FSRS_PARAMETERS,
  desiredRetention = DEFAULT_PLANNER_TARGET,
  config = {},
}) => {
  const replayed = replayFsrsHistory({
    historyRows,
    sideShown,
    bothSidesMode,
    weights,
    desiredRetention,
    config,
  });
  return {
    ...projectFsrsStateAtTime({
      state: replayed.state,
      now,
      weights,
      desiredRetention,
      config,
    }),
    filteredHistory: replayed.filteredHistory,
  };
};

const normalizeHistoricalAction = (review) =>
  getActionKey(
    review?.card_id,
    review?.both_sides_mode ? !!review?.side_shown : false
  );

export const evaluateFsrsParameters = ({
  reviews,
  weights,
  desiredRetention = DEFAULT_PLANNER_TARGET,
  config = {},
}) => {
  const normalizedWeights = normalizeFsrsParameters(weights);
  const orderedReviews = sortReviews(reviews);
  const normalizedConfig = normalizeFsrsConfig({
    ...config,
    desiredRetention,
    easyThresholdSeconds:
      config?.easyThresholdSeconds ??
      computeEasyThresholdSecondsFromReviews(orderedReviews),
  });
  const actionState = new Map();
  let totalSquaredError = 0;
  let countedReviews = 0;

  orderedReviews.forEach((review) => {
    const actionKey = normalizeHistoricalAction(review);
    const currentState = actionState.get(actionKey) ?? null;
    const predictedProbability = getCurrentRecallProbability({
      state: currentState,
      now: getSubmittedTime(review),
      weights: normalizedWeights,
    });
    const actual = Boolean(review?.was_correct) ? 1 : 0;
    totalSquaredError += Math.pow(predictedProbability - actual, 2);
    countedReviews += 1;
    actionState.set(
      actionKey,
      advanceFsrsState({
        state: currentState,
        review,
        weights: normalizedWeights,
        desiredRetention,
        config: normalizedConfig,
      })
    );
  });

  return countedReviews > 0
    ? totalSquaredError / countedReviews
    : Number.POSITIVE_INFINITY;
};

const roundWeights = (weights) => weights.map((weight) => roundNumber(weight, 6));

const projectWeightsToBounds = (weights) =>
  weights.map((weight, index) =>
    clamp(
      weight,
      LOWER_BOUNDS_PARAMETERS[index],
      UPPER_BOUNDS_PARAMETERS[index]
    )
  );

const buildOptimizerDataset = (reviews, easyThresholdSeconds = null) => {
  const groupedReviews = sortReviews(reviews).reduce((mapped, review) => {
    const actionKey = normalizeHistoricalAction(review);
    const bucket = mapped.get(actionKey) ?? [];
    bucket.push(review);
    mapped.set(actionKey, bucket);
    return mapped;
  }, new Map());

  const ratings = [];
  const deltaTs = [];
  const lengths = [];

  groupedReviews.forEach((group) => {
    if (!group.length) {
      return;
    }
    const normalizedSequence = [];
    let previousSubmittedTime = getSubmittedTime(group[0]);
    group.forEach((review, index) => {
      const submittedTime = getSubmittedTime(review);
      const deltaDays =
        index === 0
          ? 0
          : Math.max(
              0,
              Math.round((submittedTime - previousSubmittedTime) / SECONDS_PER_DAY)
            );
      normalizedSequence.push({
        rating: getReviewRating(review, easyThresholdSeconds),
        deltaDays,
      });
      previousSubmittedTime = submittedTime;
    });

    if (normalizedSequence.length < 2) {
      return;
    }

    const hasPositiveDelta = normalizedSequence
      .slice(1)
      .some((entry) => entry.deltaDays > 0);
    if (!hasPositiveDelta) {
      return;
    }

    lengths.push(normalizedSequence.length);
    normalizedSequence.forEach((entry) => {
      ratings.push(entry.rating);
      deltaTs.push(entry.deltaDays);
    });
  });

  return {
    ratings: new Uint32Array(ratings),
    deltaTs: new Uint32Array(deltaTs),
    lengths: new Uint32Array(lengths),
  };
};

export const optimizeFsrsParameters = async ({
  reviews,
  initialWeights,
  desiredRetention = DEFAULT_PLANNER_TARGET,
  config = {},
}) => {
  const baseWeights = normalizeFsrsParameters(initialWeights);
  const easyThresholdSeconds = computeEasyThresholdSecondsFromReviews(reviews);
  const normalizedConfig = normalizeFsrsConfig({
    ...config,
    desiredRetention,
    easyThresholdSeconds:
      config?.easyThresholdSeconds ?? easyThresholdSeconds,
  });
  if ((reviews?.length ?? 0) < 100) {
    return roundWeights(baseWeights);
  }

  const ready = await initFsrsBrowser();
  const fsrsBrowser = ready ? fsrsBrowserModule : null;
  if (!fsrsBrowser?.Fsrs) {
    logFsrsModeOnce(fsrsOptimizerModeLogged, "[fsrs] optimizer=local-noop-fallback");
    return roundWeights(baseWeights);
  }
  logFsrsModeOnce(fsrsOptimizerModeLogged, "[fsrs] optimizer=fsrs-browser");

  const { ratings, deltaTs, lengths } = buildOptimizerDataset(
    reviews,
    normalizedConfig.easyThresholdSeconds
  );
  if (ratings.length === 0 || lengths.length === 0) {
    logFsrsModeOnce(
      fsrsOptimizerModeLogged,
      "[fsrs] optimizer=local-noop-fallback (no valid training items)"
    );
    return roundWeights(baseWeights);
  }

  await new Promise((resolve) => {
    setTimeout(resolve, 0);
  });

  const scheduler = new fsrsBrowser.Fsrs(Float32Array.from(baseWeights));
  try {
    const optimizedWeights = scheduler.computeParameters(
      ratings,
      deltaTs,
      lengths,
      null,
      normalizedConfig.enableShortTerm,
      null,
      normalizedConfig.relearningStepsMinutes.length
    );
    return roundWeights(
      projectWeightsToBounds(Array.from(optimizedWeights ?? baseWeights))
    );
  } catch (error) {
    console.error("fsrs-browser parameter optimization failed", error);
    return roundWeights(baseWeights);
  } finally {
    try {
      scheduler.free?.();
    } catch (cleanupError) {
      console.warn("fsrs-browser cleanup failed", cleanupError);
    }
  }
};

export {
  DEFAULT_ENABLE_FUZZ,
  DEFAULT_ENABLE_SHORT_TERM,
  DEFAULT_FSRS_PARAMETERS,
  DEFAULT_LEARNING_STEPS_MINUTES,
  DEFAULT_MAXIMUM_INTERVAL_DAYS,
  DEFAULT_PLANNER_TARGET,
  DEFAULT_RELEARNING_STEPS_MINUTES,
};
